import { api } from "./api";

export type AgentDashboardSummary = {
  reserved_total_cents: number;
  approved_total_cents: number;
  last_30_days_cents: number;
  last_order_date: string | null;
};

export type AgentInvite = {
  id: string;
  invite_code: string;
  label: string | null;
  is_default: boolean;
  is_active: boolean;
  clicks_count: number;
  signups_count: number;
  created_at: string;
  expires_at: string | null;
  ref_link: string;
};

export type AgentCommission = {
  id: string;
  wc_order_id: number;
  wc_product_id: number;
  order_date: string;
  tier_level: number;
  commission_amount_cents: number;
  commission_rate: string;
  product_amount_cents: number;
  // Marge-Felder (Welle 9) — null bei sales_price-Modus
  cost_of_goods_cents: number | null;
  margin_amount_cents: number | null;
  calc_basis: "sales_price" | "margin" | null;
  // Kunde (Welle 11) — null wenn nicht in customers-Table gefunden
  customer_first_name: string | null;
  customer_last_name: string | null;
  customer_company: string | null;
  status: "reserved" | "approved" | "paid" | "clawed_back" | "cancelled";
  reserved_until: string;
  paid_at: string | null;
};

export type AgentCustomer = {
  attribution_id: string;
  wc_customer_id: number | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  city: string | null;
  attributed_at: string;
  attribution_source: "cookie" | "manual_admin" | "import" | "rolled_up";
  orders_count: number;
  total_earned_cents: number;
  last_order_at: string | null;
};

export const listMyCustomers = (): Promise<AgentCustomer[]> =>
  api.get<AgentCustomer[]>("/agent/customers");

export type AgentPayout = {
  id: string;
  period_start: string;
  period_end: string;
  commissions_amount_cents: number;
  clawbacks_amount_cents: number;
  net_amount_cents: number;
  status: "draft" | "approved" | "sent_to_easybill" | "paid" | "failed" | "cancelled";
  paid_at: string | null;
  payment_reference: string | null;
  easybill_credit_note_id: string | null;
  created_at: string;
};

export type WCProduct = {
  id: number;
  name: string;
  permalink: string;
  price: string | null;
  image_src: string | null;
  short_description: string | null;
  type: "simple" | "variable";
  /** Geschätzter EUR-Verdienst für diesen Agent bei einem Verkauf (Welle 10). */
  estimated_earning_cents: number | null;
  estimated_earning_basis: "margin" | "sales_price" | null;
  /** Verfügbarkeits-Status direkt aus WC (Welle 10). */
  stock_status: "instock" | "outofstock" | "onbackorder" | null;
  purchasable: boolean;
  stock_quantity: number | null;
};

export type WCProductSearchResponse = {
  items: WCProduct[];
  total: number;
  total_pages: number;
  page: number;
  per_page: number;
};

export type GeneratedLink = {
  url: string;
  product_id: number;
  product_name: string;
  invite_code: string;
};

export const getDashboardSummary = () =>
  api.get<AgentDashboardSummary>("/agent/dashboard-summary");

export const listMyInvites = () => api.get<AgentInvite[]>("/agent/invites");

export const createInvite = (label: string, codeSuffix?: string) =>
  api.post<AgentInvite>("/agent/invites", {
    label,
    ...(codeSuffix ? { code_suffix: codeSuffix } : {}),
  });

export const deleteInvite = (id: string) => api.delete<void>(`/agent/invites/${id}`);

export const listMyCommissions = (limit = 100, offset = 0) =>
  api.get<AgentCommission[]>(`/agent/commissions?limit=${limit}&offset=${offset}`);

export const listMyPayouts = () => api.get<AgentPayout[]>("/agent/payouts");

export const searchProducts = (
  options: { search?: string; perPage?: number; page?: number; onlySimple?: boolean } = {},
) => {
  const q = new URLSearchParams();
  if (options.search) q.set("search", options.search);
  q.set("per_page", String(options.perPage ?? 20));
  q.set("page", String(options.page ?? 1));
  if (options.onlySimple) q.set("only_simple", "true");
  return api.get<WCProductSearchResponse>(`/agent/products?${q.toString()}`);
};

export const generateInviteSuffix = (labelHint = "") =>
  api.get<{ suffix: string; full_code: string }>(
    `/agent/invites/generate-suffix${labelHint ? `?label_hint=${encodeURIComponent(labelHint)}` : ""}`,
  );

export const generateProductLink = (
  productId: number,
  inviteCode: string,
  campaignLabel?: string,
) =>
  api.post<GeneratedLink>("/agent/links/generate", {
    product_id: productId,
    invite_code: inviteCode,
    ...(campaignLabel ? { campaign_label: campaignLabel } : {}),
  });

export type TeamMember = {
  id: string;
  agent_code: string;
  name: string;
  status: "pending" | "active" | "suspended" | "terminated";
  joined_at: string;
  recruited_by_name: string | null;
  share_rate: number | null;           // individueller Satz (null = Team-Default)
  effective_share_rate: number;        // was tatsächlich gilt
  recruiter_bonus_rate: number | null; // Bonus an den Werber (null = keiner)
  bonus_editable: boolean;             // false = vom Master selbst geworben
  my_reserved_cents: number;
  my_approved_cents: number;
  my_paid_cents: number;
  my_total_cents: number;
};

export type MyRecruit = {
  name: string;
  bonus_rate: number | null; // null = Master hält Sätze privat
};

export type TeamResponse = {
  is_master: boolean;
  join_slug?: string;                  // vorname-nachname für /join/<slug>
  master_first_name: string | null;    // Mitglied-Sicht
  my_share_rate: number | null;        // Mitglied-Sicht (null = Master hält Sätze privat)
  my_recruits?: MyRecruit[];           // Mitglied-Sicht: von mir Geworbene + mein Bonus
  team_default_share_rate?: number | null;
  system_default_share_rate?: number;
  effective_default_share_rate?: number;
  share_visible?: boolean;
  members: TeamMember[];
  can_recruit: boolean;
};

export const getMyTeam = () => api.get<TeamResponse>("/agent/team");

export const setMemberShareRate = (memberId: string, rate: number | null) =>
  api.put<{ ok: boolean; rate: number | null }>(`/agent/team/${memberId}/share-rate`, { rate });

export const setRecruiterBonus = (memberId: string, rate: number | null) =>
  api.put<{ ok: boolean; rate: number | null }>(`/agent/team/${memberId}/recruiter-bonus`, { rate });

export const updateTeamSettings = (body: {
  default_share_rate?: number;
  clear_default?: boolean;
  share_visible?: boolean;
}) => api.put<{ ok: boolean }>(`/agent/team-settings`, body);

// Light → Voll Upgrade
export type UpgradeStatus = {
  agent_type: "light" | "full";
  kyc_status: "not_required" | "pending" | "verified" | "rejected";
  can_request: boolean;
};

export type UpgradePayload = {
  iban: string;
  bic?: string;
  is_kleinunternehmer: boolean;
  ust_id?: string;
  address_line1: string;
  postal_code: string;
  city: string;
  country_code?: string;
  phone?: string;
};

export const getUpgradeStatus = () => api.get<UpgradeStatus>("/agent/upgrade-status");

export const submitUpgrade = (payload: UpgradePayload) =>
  api.post<UpgradeStatus>("/agent/upgrade-request", payload);

// Stats
export type StatsTopProduct = {
  wc_product_id: number;
  product_name?: string | null;
  event_count: number;
  total_cents: number;
};

export type StatsByDay = {
  day: string;
  cents: number;
};

export type StatsClicksByDay = {
  day: string;
  clicks: number;
};

export type StatsPrevious = {
  clicks: number;
  earned_cents: number;
};

export type AgentStats = {
  period_days: number;
  total_clicks: number;
  total_signups: number;
  conversion_rate: number;
  total_earned_cents: number;
  earnings_by_status: Record<string, number>;
  top_products: StatsTopProduct[];
  earnings_by_day: StatsByDay[];
  clicks_by_day?: StatsClicksByDay[];
  previous?: StatsPrevious | null;
};

export const getMyStats = (periodDays = 30) =>
  api.get<AgentStats>(`/agent/stats?period_days=${periodDays}`);

export const myPayoutPdfUrl = (payoutId: string): string => {
  const base = import.meta.env.VITE_API_BASE || "";
  return `${base}/agent/payouts/${payoutId}/pdf`;
};

export type ProfileUpdate = {
  first_name?: string;
  last_name?: string;
  phone?: string;
  address_line1?: string;
  address_line2?: string;
  postal_code?: string;
  city?: string;
  country_code?: string;
  /** Pool-Modell: 0.0 - 0.5 (=0-50%). `null` = Override loeschen, Default nutzen. */
  sponsor_share_rate?: number | null;
};

export const updateProfile = (payload: ProfileUpdate) =>
  api.put<{ ok: boolean; updated_fields: string[] }>("/agent/me/profile", payload);

export type PoolInfo = {
  sponsor_share_rate: number | null;
  effective_sponsor_share_rate: number;
  default_top_agent_rate: number;
  default_sponsor_share_rate: number;
  max_sponsor_share_rate: number;
};

export const getMyPoolInfo = () =>
  api.get<PoolInfo>("/agent/me/pool-info");

export const changeMyPassword = (currentPassword: string, newPassword: string) =>
  api.post<{ ok: boolean }>("/agent/me/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });

export const getShopModeLink = () =>
  api.get<{ url: string; invite_code: string; valid_seconds: number }>(
    "/agent/shop-mode-link",
  );

// --- Kunden-Verzeichnis (per-Agent vom Admin freigeschaltet) -----------------

export type DirectoryCustomer = {
  name: string;
  firma: string;
  strasse: string;
  plz: string;
  ort: string;
};

export type CustomerDirectoryResponse = {
  enabled: boolean;
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
  cities: string[];
  items: DirectoryCustomer[];
};

export const getCustomerDirectoryStatus = () =>
  api.get<{ enabled: boolean }>("/agent/customer-directory/status");

export const getCustomerDirectory = (params: {
  city?: string;
  q?: string;
  page?: number;
  per_page?: number;
}) => {
  const qs = new URLSearchParams();
  if (params.city) qs.set("city", params.city);
  if (params.q) qs.set("q", params.q);
  qs.set("page", String(params.page ?? 1));
  qs.set("per_page", String(params.per_page ?? 50));
  return api.get<CustomerDirectoryResponse>(`/agent/customer-directory?${qs}`);
};


// ---- Online-Prospekt (DP Tools) ----
export type AgentProspekt = {
  id: string;
  title: string;
  subtitle: string;
  valid_from: string;
  valid_to: string;
  items_count: number;
  updated_at: string | null;
  public_url: string;
  share_url: string;
  pdf_url: string;
  cover_url: string;
  views_total: number;
  clicks_total: number;
  my_views: number;
  my_clicks: number;
};
export type AgentProspekteResponse = {
  items: AgentProspekt[];
  code: string;
  register_url: string;
  cta_enabled: boolean;
};
export const listMyProspekte = () => api.get<AgentProspekteResponse>("/agent/prospekte");
export const setMyProspektCta = (enabled: boolean) =>
  api.put<{ ok: boolean; enabled: boolean }>("/agent/me/prospekt-cta", { enabled });
